import { Sitrep } from '../types/incident';
import { API_BASE, getApiHeaders } from './apiClient';

async function apiFetch<T>(path: string, options?: RequestInit): Promise<T> {
  const res  = await fetch(`${API_BASE}${path}`, {
    headers: { ...getApiHeaders(), ...options?.headers },
    ...options,
  });
  const text = await res.text();
  let data: any;
  try { data = JSON.parse(text); } catch { throw new Error(`Server error ${res.status}`); }
  if (!res.ok) throw new Error(data.error || `Request failed: ${res.status}`);
  return data as T;
}

export async function getSitrep(incidentId: string): Promise<Sitrep | null> {
  const data = await apiFetch<{ sitrep: Sitrep | null }>(`/cima/incidents/${incidentId}/sitrep`);
  return data.sitrep;
}

export async function updateSitrepField(
  incidentId: string,
  field:      string,
  value:      any,
): Promise<Sitrep> {
  const data = await apiFetch<{ sitrep: Sitrep }>(
    `/cima/incidents/${incidentId}/sitrep`,
    { method: 'PATCH', body: JSON.stringify({ field, value }) },
  );
  return data.sitrep;
}

export async function updateFullSitrep(
  incidentId: string,
  sitrep:     Partial<Sitrep>,
): Promise<Sitrep> {
  const data = await apiFetch<{ sitrep: Sitrep }>(
    `/cima/incidents/${incidentId}/sitrep`,
    { method: 'PATCH', body: JSON.stringify({ sitrep }) },
  );
  return data.sitrep;
}

/**
 * Pull the LIVE incident as a modern HTML report — sibling to the XLSX,
 * same data but rendered as a polished web document with 11 tabbed
 * sections, animations, and print stylesheet. Single self-contained
 * file. Two modes:
 *   - download = true : trigger a file download (.html)
 *   - download = false: open in a new tab for in-browser preview
 */
export async function downloadIncidentHtmlReport(
  incidentId: string,
  opts: { download?: boolean } = {},
): Promise<void> {
  const url = `${API_BASE}/cima/incidents/${incidentId}/sitrep/report.html${opts.download ? '?download=1' : ''}`;
  const res = await fetch(url, { headers: getApiHeaders() });
  if (!res.ok) {
    const text = await res.text();
    throw new Error(text || `HTTP ${res.status}`);
  }
  const html = await res.text();
  const blob = new Blob([html], { type: 'text/html;charset=utf-8' });
  const objectUrl = URL.createObjectURL(blob);
  if (opts.download) {
    const filename = (res.headers.get('content-disposition') || '').match(/filename="([^"]+)"/)?.[1]
      || `IncidentReport_${incidentId}.html`;
    const a = document.createElement('a');
    a.href = objectUrl;
    a.download = filename;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
  } else {
    // Open in a new tab — the auth header was already sent via fetch,
    // so the new tab loads the cached blob URL (no CORS / re-auth).
    window.open(objectUrl, '_blank');
  }
  // Defer revoke so the new tab has time to load the blob URL.
  setTimeout(() => URL.revokeObjectURL(objectUrl), 60_000);
}

/**
 * Pull the LIVE incident workbook (CIMT contacts, IAP, Stakeholders,
 * SitRep, Severity Matrix) and trigger a browser download. Used by the
 * schedule-meeting form's "Download latest XLSX" button so the IC can
 * preview what invitees will receive without having to schedule first.
 *
 * Same base64-JSON decode pattern as the per-version download — API
 * Gateway mangles raw binary in transit.
 */
export async function downloadLiveSitrepXlsx(incidentId: string): Promise<void> {
  const res = await fetch(`${API_BASE}/cima/incidents/${incidentId}/sitrep/xlsx`, {
    headers: getApiHeaders(),
  });
  if (!res.ok) {
    const text = await res.text();
    throw new Error(text || `HTTP ${res.status}`);
  }
  const json = await res.json();
  const binary = atob(json.data);
  const bytes = new Uint8Array(binary.length);
  for (let i = 0; i < binary.length; i++) bytes[i] = binary.charCodeAt(i);
  const blob = new Blob([bytes], { type: json.contentType });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = json.filename || `SitRep_${incidentId}.xlsx`;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  URL.revokeObjectURL(url);
}
